import Typography from '../components/Typography';
import Layout from '../components/Layout';
import cx from 'classnames';
import { useNavigate } from '@reach/router';

const NotifiedBanner = ({ senderName = 'Sara', senderInitial = 'S' }) => {
  return (
    <div
      className="bg-[#e9f1ea] content-stretch flex gap-[8px] items-center justify-center py-[12px] relative w-full rounded-b-[12px]"
      data-name="Background"
      data-node-id="953:33390"
    >
      {/* Avatar */}
      <div
        className="bg-[#141b34] content-stretch flex items-center justify-center relative rounded-[9999px] shrink-0 size-[24px]"
        data-name="Background"
        data-node-id="953:33391"
      >
        <div
          className="flex flex-col font-['Montserrat:Bold',sans-serif] font-bold justify-center leading-[0] relative shrink-0 text-[10px] text-center text-white whitespace-nowrap"
          data-node-id="953:33392"
        >
          <p className="leading-[15px]">{senderInitial}</p>
        </div>
      </div>

      {/* Text Content */}
      <div
        className="content-stretch flex flex-col items-start relative shrink-0"
        data-name="Container"
        data-node-id="953:33393"
      >
        <div
          className="flex flex-col font-['Montserrat:SemiBold',sans-serif] font-semibold justify-center leading-[0] relative shrink-0 text-[#141b34] text-[14px] whitespace-nowrap"
          data-node-id="953:33394"
        >
          <p>
            <span className="leading-[20px]">{`${senderName} has been notified `}</span>
            <span className="font-['Montserrat:Bold',sans-serif] font-bold leading-[20px] text-[#16a34a]">
              ♥
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

const ReusableCardIcon = ({ size = 48 }) => {
  // Image asset from Figma (valid for 7 days)
  const imgIcon =
    'https://www.figma.com/api/mcp/asset/499f0375-7374-4efe-a378-f66c312ec289';

  return (
    <div
      className="backdrop-blur-[2px] bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.1)] border-solid content-stretch flex items-center justify-center p-px relative rounded-[12px]"
      style={{ width: `${size}px`, height: `${size}px` }}
      data-name="Overlay+Border+OverlayBlur"
      data-node-id="953:33451"
    >
      <div
        className="relative shrink-0"
        data-name="Container"
        data-node-id="953:33452"
      >
        <div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex flex-col items-start py-[2px] relative">
          <div className="flex items-center justify-center relative shrink-0">
            <div className="-scale-y-100 flex-none">
              <div
                className="h-[28px] relative w-[24.02px]"
                data-name="Icon"
                data-node-id="953:33453"
              >
                <img
                  alt="Reusable"
                  className="block max-w-none size-full"
                  src={imgIcon}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReusableCard = () => {
  return (
    <div className="bg-[#141B34] p-6 rounded-2xl">
      <ReusableCardIcon />
      <div className="mb-4" />
      <Typography color="white" weight="bold" className="text-[20px]">
        This card is reusable!
      </Typography>
      <div className="mb-3" />
      <Typography color="lightblue" weight="medium" className="text-[14px]">
        When you are ready to gift it, simply scan the QR code to reload and
        pass it on.
      </Typography>
    </div>
  );
};

const CelebrationIcon = () => {
  return (
    <div
      className="content-stretch flex flex-col items-center relative w-24 mx-auto"
      data-name="Container"
      data-node-id="953:33404"
    >
      {/* Main Icon Container */}
      <div
        className="bg-[#f9fafb] content-stretch flex items-center justify-center relative rounded-[9999px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] shrink-0 size-[96px]"
        data-name="Background+Shadow"
        data-node-id="953:33405"
      >
        <div
          className="content-stretch flex flex-col items-start relative shrink-0"
          data-name="Container"
          data-node-id="953:33406"
        >
          <div
            className="flex flex-col font-['Montserrat:Regular',sans-serif] font-normal justify-center leading-[0] relative shrink-0 text-[48px] text-black whitespace-nowrap"
            data-node-id="953:33407"
          >
            <p className="leading-[48px]">🎉</p>
          </div>
        </div>
      </div>

      {/* Top-right Star */}
      <div
        className="absolute content-stretch flex flex-col items-start right-[-0.72px] top-[-4px]"
        data-name="Container"
        data-node-id="953:33408"
      >
        <div
          className="flex flex-col font-['Montserrat:Regular','Noto_Sans:Regular',sans-serif] font-normal justify-center leading-[0] relative shrink-0 text-[#facc15] text-[18px] whitespace-nowrap"
          data-node-id="953:33409"
        >
          <p className="leading-[28px]">✦</p>
        </div>
      </div>

      {/* Bottom-left Dot */}
      <div
        className="absolute bottom-[4px] content-stretch flex flex-col items-start left-[-4px]"
        data-name="Container"
        data-node-id="953:33410"
      >
        <div
          className="flex flex-col font-['Montserrat:Regular','Noto_Sans:Regular',sans-serif] font-normal justify-center leading-[0] relative shrink-0 text-[#60a5fa] text-[14px] whitespace-nowrap"
          data-node-id="953:33411"
        >
          <p className="leading-[20px]">●</p>
        </div>
      </div>
    </div>
  );
};

const HowItWorks = () => {
  // Image assets from Figma (valid for 7 days)
  const imgIcon1 =
    'https://www.figma.com/api/mcp/asset/c7a76781-323e-4e23-9488-660769391c94';
  const imgIcon2 =
    'https://www.figma.com/api/mcp/asset/e6b19b01-5aac-4502-90c3-b1202945baa6';
  const imgIcon3 =
    'https://www.figma.com/api/mcp/asset/4e495200-1c81-4031-81af-cabc59707575';

  const steps = [
    {
      icon: imgIcon1,
      text: 'Scan the QR code on the back.',
      dataNodeId: '953:33417',
      multiline: false,
    },
    {
      icon: imgIcon2,
      text: "Load any amount you'd like.",
      dataNodeId: '953:33425',
      multiline: false,
    },
    {
      icon: imgIcon3,
      text: ['Send it to a friend or someone you', 'care about.'],
      dataNodeId: '953:33433',
      multiline: true,
    },
  ];

  return (
    <div
      className="content-stretch flex flex-col gap-[24px] items-start relative w-full"
      data-name="Container"
      data-node-id="953:33413"
    >
      {/* Heading */}
      <div
        className="content-stretch flex flex-col items-start pl-[4px] relative shrink-0 w-full"
        data-name="Heading 3"
        data-node-id="953:33414"
      >
        <Typography
          weight="bold"
          className="text-[#9ca3af] text-[12px] tracking-[1.8px] uppercase w-full leading-[16px]"
          data-node-id="953:33415"
        >
          HOW IT WORKS
        </Typography>
      </div>

      {/* Steps */}
      <div
        className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full"
        data-name="Container"
        data-node-id="953:33416"
      >
        {steps.map((step, index) => (
          <div
            key={index}
            className="content-stretch flex items-center relative shrink-0 w-full"
            data-name="Container"
            data-node-id={step.dataNodeId}
          >
            {/* Icon Container */}
            <div
              className="content-stretch flex flex-col h-[40px] items-start pr-[16px] relative shrink-0 w-[56px]"
              data-name="Margin"
              data-node-id={`${step.dataNodeId}-margin`}
            >
              <div
                className="bg-[#e9f1ea] content-stretch flex items-center justify-center relative rounded-[9999px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] shrink-0 size-[40px]"
                data-name="Background+Shadow"
                data-node-id={`${step.dataNodeId}-bg`}
              >
                <div
                  className="content-stretch flex flex-col items-start py-[2px] relative shrink-0"
                  data-name="Container"
                  data-node-id={`${step.dataNodeId}-container`}
                >
                  <div className="flex items-center justify-center relative shrink-0">
                    <div className="-scale-y-100 flex-none">
                      <div
                        className="h-[24px] relative w-[20px]"
                        data-name="Icon"
                        data-node-id={`${step.dataNodeId}-icon`}
                      >
                        <img
                          alt={`Step ${index + 1}`}
                          className="block max-w-none size-full"
                          src={step.icon}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Text Content */}
            <div
              className={cx(
                'content-stretch flex flex-col items-start relative shrink-0',
                {
                  'pb-[0.75px] pr-[19.81px]': step.multiline,
                },
              )}
              data-name="Container"
              data-node-id={`${step.dataNodeId}-text`}
            >
              {step.multiline ? (
                <Typography
                  weight="medium"
                  className="text-[#374151] text-[14px] leading-[17.5px]"
                  data-node-id={`${step.dataNodeId}-text-content`}
                >
                  {step.text.map((line, lineIndex) => (
                    <p
                      key={lineIndex}
                      className={lineIndex === 0 ? 'mb-0' : ''}
                    >
                      {line}
                    </p>
                  ))}
                </Typography>
              ) : (
                <Typography
                  weight="medium"
                  className="text-[#374151] text-[14px] whitespace-nowrap leading-[17.5px]"
                  data-node-id={`${step.dataNodeId}-text-content`}
                >
                  {step.text}
                </Typography>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const GiftConfirmation = () => {
  const navigate = useNavigate();
  return (
    <Layout className="px-6">
      <NotifiedBanner />
      <div className="mb-6" />
      <CelebrationIcon />
      <div className="mb-6" />
      <Typography weight="black" className="mb-4 text-[30px] text-center">
        You did it!
      </Typography>
      <Typography className="mb-8 text-[18px] text-center" color="secondary">
        $50 to One Tree Planted
      </Typography>
      <ReusableCard />
      <div className="mb-8" />
      <HowItWorks />
      <div className="mb-6" />
      <button
        className="cz-btn-brand w-full"
        onClick={() => navigate(`/gift-redeem/create-account`)}
      >
        I'm Ready to Gift!
      </button>
    </Layout>
  );
};

export default GiftConfirmation;
